/**
 * Breadcrumb utilities for PageBuilder
 *
 * This module provides functionality to automatically generate breadcrumbs for editorial pages.
 *
 * Features:
 * - Configuration-based detection: Checks if a page ID is in the editorial configuration
 * - URL parameter detection: Supports ?from=editorials query parameter for flexible navigation
 * - Automatic category extraction from page IDs (e.g., "20250607_home-for-icons_press" -> "Press")
 *
 * Usage:
 * 1. Configure editorial page IDs in EDITORIALS_CONFIG or pass custom config
 * 2. PageBuilder automatically detects editorial pages and shows breadcrumbs
 * 3. EditorialsPage adds ?from=editorials parameter when linking to articles
 */

// Default editorial configuration - can be overridden by passing config
const DEFAULT_EDITORIALS_CONFIG = {
  layout: {
    editorialsPage: {
      pageIds: [
        '20250607_home-for-icons_press',
        '20250616_loose-ends-004_editorial',
        '20250603_loose-ends-003_editorial',
        '20250602_loose-ends-002_editorial',
        '20250420_loose-ends-001_editorial',
        '20250405_new-era_press',
        '20250122_going-live_press',
      ],
    },
  },
};

/**
 * Get editorial page IDs from config
 * @param {Object} config - Configuration object
 * @returns {Array} Array of editorial page IDs
 */
export const getEditorialPageIds = (config = DEFAULT_EDITORIALS_CONFIG) => {
  const configuredIds = config?.layout?.editorialsPage?.pageIds;

  if (
    configuredIds &&
    Array.isArray(configuredIds) &&
    configuredIds.length > 0
  ) {
    return configuredIds;
  }

  // Fallback to default
  return DEFAULT_EDITORIALS_CONFIG.layout.editorialsPage.pageIds;
};

/**
 * Check if a page ID is an editorial page
 * @param {string} pageId - The page ID to check
 * @param {Object} config - Configuration object (optional)
 * @returns {boolean} True if the page is an editorial page
 */
export const isEditorialPage = (pageId, config) => {
  if (!pageId) return false;

  const editorialPageIds = getEditorialPageIds(config);
  return editorialPageIds.includes(pageId);
};

/**
 * Extract category from page ID (e.g., "20250607_home-for-icons_press" -> "Press")
 * @param {string} pageId - The page ID
 * @returns {string} The category name
 */
export const getCategoryFromPageId = pageId => {
  if (!pageId) return 'Editorial';

  const parts = pageId.split('_');
  if (parts.length > 1) {
    const category = parts[parts.length - 1];
    // Capitalize first letter only, keep rest lowercase
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  }

  return 'Editorial';
};

/**
 * Generate breadcrumb items for editorial pages
 * @param {string} pageId - Current page ID
 * @param {Object} pageData - Page data containing metadata
 * @param {Object} config - Configuration object (optional)
 * @param {Object} location - Location object from router (optional)
 * @returns {Array|null} Array of breadcrumb items or null if not an editorial page
 */
export const getEditorialBreadcrumbs = (pageId, pageData, config, location) => {
  // Check if page is editorial by config OR by URL parameter
  const isEditorialByConfig = isEditorialPage(pageId, config);
  const isEditorialByUrl = location?.search?.includes('from=editorials');

  if (!isEditorialByConfig && !isEditorialByUrl) {
    return null;
  }

  const category = getCategoryFromPageId(pageId);
  const pageTitle = pageData?.meta?.pageTitle?.content || 'Article';

  return [
    {
      label: 'News',
      name: 'EditorialsPage',
      params: {},
    },
    {
      label: pageTitle,
      isLast: true,
    },
  ];
};

/**
 * Generate breadcrumb items with custom parent page
 * @param {string} parentLabel - Label for the parent page
 * @param {string} parentRouteName - Route name for the parent page
 * @param {Object} parentRouteParams - Parameters for the parent route
 * @param {string} currentPageTitle - Title of the current page
 * @returns {Array} Array of breadcrumb items
 */
export const generateCustomBreadcrumbs = (
  parentLabel,
  parentRouteName,
  parentRouteParams = {},
  currentPageTitle = 'Current Page'
) => {
  return [
    {
      label: parentLabel,
      name: parentRouteName,
      params: parentRouteParams,
    },
    {
      label: currentPageTitle,
      isLast: true,
    },
  ];
};
